<template>
  <aside class="logs-panel" data-testid="logs-side-panel">
    <LogsPanelHeader
      :auto-scroll="autoScrollEnabled"
      :category-count="subTabs.length"
      @toggle-auto-scroll="autoScrollEnabled = !autoScrollEnabled"
      @create-ticket="onCreateTicket"
    />

    <div v-if="status === 'ready'" class="logs-panel__subtabs" role="tablist" data-testid="logs-subtabs">
      <button
        v-for="tab in subTabs"
        :key="tab.id"
        type="button"
        role="tab"
        :aria-selected="activeTab === tab.id"
        :data-testid="`logs-subtab-${tab.id}`"
        class="logs-panel__subtab"
        :class="{ 'logs-panel__subtab--active': activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        <Icon :name="tab.icon" size="xs" />
        <span>{{ tab.label }}</span>
      </button>
    </div>

    <div class="logs-panel__content">
      <LogsPanelLoading v-if="status === 'loading' || status === 'idle'" />
      <LogsPanelError v-else-if="renderError" message="Couldn't display the logs in this archive." :retryable="false" />
      <LogsPanelError v-else-if="status === 'error'" :message="errorMessage" :retryable="errorRetryable" @retry="retry" />
      <LogsPanelEmpty v-else-if="status === 'empty'" />
      <template v-else>
        <NetworkDetailPanel
          v-if="activeTab === 'network' && selectedNetworkEntry"
          :entry="selectedNetworkEntry"
          @close="clearSelectedNetworkEntry"
          @jump="onSeek"
        />
        <template v-else>
          <BrowserInfoTab v-if="activeTab === 'browser'" />
          <ConsoleLogList v-else-if="activeTab === 'console'" :auto-scroll="autoScrollEnabled" @seek="onSeek" />
          <NetworkLogList v-else-if="activeTab === 'network'" :auto-scroll="autoScrollEnabled" />
          <ErrorsLogList v-else-if="activeTab === 'errors'" :auto-scroll="autoScrollEnabled" @seek="onSeek" />
        </template>
      </template>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, watch, onErrorCaptured } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon } from '@/storybook';
import LogsPanelHeader from './LogsPanelHeader.vue';
import LogsPanelLoading from './LogsPanelLoading.vue';
import LogsPanelError from './LogsPanelError.vue';
import LogsPanelEmpty from './LogsPanelEmpty.vue';
import BrowserInfoTab from './BrowserInfoTab.vue';
import ConsoleLogList from './ConsoleLogList.vue';
import NetworkLogList from './NetworkLogList.vue';
import ErrorsLogList from './ErrorsLogList.vue';
import NetworkDetailPanel from './NetworkDetailPanel.vue';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useArtifactsStore } from '@/modules/viewer/store/useArtifactsStore';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useLogsStore } from '@/modules/viewer/store/useLogsStore';
import type { LogSubTab } from '@/modules/viewer/types/logs';
import { useTrack, TrackEvents } from '@/composables/useTrack';

const subTabs: Array<{ id: LogSubTab; label: string; icon: string }> = [
  { id: 'browser', label: 'Browser info', icon: 'general.info' },
  { id: 'console', label: 'Console', icon: 'general.file' },
  { id: 'network', label: 'Network', icon: 'general.globe' },
  { id: 'errors', label: 'Errors', icon: 'general.bug' },
];

const activeTab = ref<LogSubTab>('console');
const autoScrollEnabled = ref(true);

const viewerItemStore = useViewerItemStore();
const { insightsURL } = storeToRefs(useArtifactsStore());
const { openShareModalAction } = useItemActions();
const { track } = useTrack();

const logsStore = useLogsStore();
const { status, errorMessage, errorRetryable, selectedNetworkEntry } = storeToRefs(logsStore);
const { loadFromUrl, clearSearchForTab, clearSelectedNetworkEntry } = logsStore;

// Render-time error boundary: a bad entry in one list shouldn't break the
// whole panel. Cleared on fresh load and on tab switch so the error can't
// stick around across recoverable state changes.
const renderError = ref<Error | null>(null);
onErrorCaptured(err => {
  renderError.value = err;
  return false;
});

// Fires on mount and whenever the artifact URL changes (e.g. Pusher update).
// The store caches by URL, so toggling Video → Logs → Video → Logs is free.
watch(
  insightsURL,
  url => {
    renderError.value = null;
    loadFromUrl(url);
  },
  { immediate: true }
);

// Fire LOGS_PANEL_OPENED once the store settles on a terminal state, so the
// event carries a meaningful `outcome` (ready / empty / error) rather than
// racing the fetch.
const hasTrackedOpen = ref(false);
watch(
  status,
  newStatus => {
    if (hasTrackedOpen.value) return;
    if (newStatus === 'loading' || newStatus === 'idle') return;
    hasTrackedOpen.value = true;
    track(TrackEvents.LOGS_PANEL_OPENED, { outcome: newStatus });
  },
  { immediate: true }
);

// Each tab owns its own search state — the old tab's search is cleared on
// switch so the new tab starts fresh. Network detail and render errors are
// also reset since they only make sense in the tab they came from.
watch(activeTab, (_newTab, oldTab) => {
  if (oldTab) {
    clearSearchForTab(oldTab);
  }
  clearSelectedNetworkEntry();
  renderError.value = null;
});

const onCreateTicket = () => {
  // Deep-link into the share modal's Export tab — that's where the
  // integration/ticket-creation surfaces live.
  openShareModalAction({ modalProps: { selectedTab: '#export' } });
};

const retry = () => {
  renderError.value = null;
  loadFromUrl(insightsURL.value);
};

// 3-second lead-in gives context for the action that produced the log entry;
// autoplay ensures the user sees it even if the video was paused.
const onSeek = (timestamp: number) => {
  viewerItemStore.setVideoTime(Math.max(0, timestamp - 3), { autoplay: true });
};
</script>

<style scoped>
.logs-panel {
  @apply flex flex-col w-full h-full max-h-full bg-white dark:bg-zinc-800 rounded-10 border border-zinc-200 dark:border-zinc-700 overflow-hidden;
  min-height: 0;
}

.logs-panel__subtabs {
  @apply flex items-center gap-1 px-4 sm:px-5 border-b border-zinc-200 dark:border-zinc-700;
}

.logs-panel__subtab {
  @apply flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-bold rounded-none cursor-pointer transition-colors relative;
  @apply text-black dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100;
}

.logs-panel__subtab--active {
  @apply text-blue-500 dark:text-blue-400;
}

.logs-panel__subtab--active::after {
  content: '';
  @apply absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 dark:bg-blue-400;
}

.logs-panel__content {
  @apply flex-grow min-h-0 overflow-y-auto;
  /* Show a visible scrollbar track so users know they can scroll */
  scrollbar-width: thin;
  scrollbar-color: rgb(161 161 170) transparent; /* zinc-400 */
}

.dark .logs-panel__content,
:global(.dark) .logs-panel__content {
  scrollbar-color: rgb(82 82 91) transparent; /* zinc-600 */
}

.logs-panel__content::-webkit-scrollbar {
  width: 8px;
}

.logs-panel__content::-webkit-scrollbar-track {
  background: transparent;
}

.logs-panel__content::-webkit-scrollbar-thumb {
  background-color: rgb(161 161 170); /* zinc-400 */
  border-radius: 4px;
}

.dark .logs-panel__content::-webkit-scrollbar-thumb,
:global(.dark) .logs-panel__content::-webkit-scrollbar-thumb {
  background-color: rgb(82 82 91); /* zinc-600 */
}
</style>
